// Our own way past country blocks.
//
// Channels and films are often refused because of where the request comes from.
// Everything the media hub fetches already travels through this site's own
// relay, which clears most blocks on its own. When a channel is still refused,
// the relay can go one hop further: out through an exit point of the user's own
// — their private relay, VPN web gateway or worker — written with {url} where
// the address belongs.
//
// There are two separate lanes, and they never share a list:
//   • "adult" — the grown-up settings page (/geo-relay)
//   • "kids"  — the children's own connection (/kids-vpn)
// While the children's side is in use, media requests travel down the kids
// lane, so changing the grown-up settings cannot change what a child reaches
// and the other way round.
//
// Nothing is routed anywhere unless the user adds it here. Both lists live on
// the device only.

import { kidsDoorOpen } from "./kidsDoor";
import { kidLockActive } from "./kidGuard";

export type Lane = "adult" | "kids";

export type ExitNode = {
  id: string;
  label: string;
  /** Template containing {url}, e.g. https://my-exit.example.com/?u={url} */
  template: string;
  country?: string;
  addedAt: string;
  lastOk?: string;
  lane?: Lane;
};

const KEYS: Record<Lane, { nodes: string; on: string; active: string }> = {
  adult: { nodes: "sophia:geo:exits", on: "sophia:geo:on", active: "sophia:geo:active" },
  kids: { nodes: "sophia:kids:exits", on: "sophia:kids:geo-on", active: "sophia:kids:geo-active" },
};

/** Which lane is in force right now. */
export function currentLane(): Lane {
  try {
    if (kidsDoorOpen() || kidLockActive()) return "kids";
  } catch { /* storage unavailable */ }
  return "adult";
}

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch { return fallback; }
}
function write(key: string, value: unknown): void {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* private mode */ }
}

export function exitNodes(lane: Lane = "adult"): ExitNode[] {
  if (typeof localStorage === "undefined") return [];
  return read<ExitNode[]>(KEYS[lane].nodes, []);
}

export function geoRelayOn(lane: Lane = "adult"): boolean {
  if (typeof localStorage === "undefined") return false;
  return read<boolean>(KEYS[lane].on, true);
}
export function setGeoRelayOn(on: boolean, lane: Lane = "adult"): void { write(KEYS[lane].on, on); }

export function activeExitId(lane: Lane = "adult"): string | null {
  if (typeof localStorage === "undefined") return null;
  return read<string | null>(KEYS[lane].active, null);
}
export function setActiveExit(id: string | null, lane: Lane = "adult"): void { write(KEYS[lane].active, id); }

/** A template is usable when it is https, public, and names where the address goes. */
export function templateValid(template: string): boolean {
  if (!template.includes("{url}") || template.length > 400) return false;
  try {
    const u = new URL(template.replace("{url}", "https%3A%2F%2Fexample.com%2F"));
    if (u.protocol !== "https:") return false;
    const h = u.hostname.toLowerCase();
    if (h === "localhost" || /^127\./.test(h) || /^10\./.test(h) || /^192\.168\./.test(h)) return false;
    return true;
  } catch { return false; }
}

export function addExitNode(
  input: { label: string; template: string; country?: string },
  lane: Lane = "adult",
): ExitNode | null {
  if (!templateValid(input.template)) return null;
  const node: ExitNode = {
    id: `x-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
    label: input.label.trim() || "My exit point",
    template: input.template.trim(),
    country: input.country?.trim() || undefined,
    addedAt: new Date().toISOString(),
    lane,
  };
  const list = [...exitNodes(lane), node];
  write(KEYS[lane].nodes, list);
  if (!activeExitId(lane)) setActiveExit(node.id, lane);
  return node;
}

export function removeExitNode(id: string, lane: Lane = "adult"): void {
  write(KEYS[lane].nodes, exitNodes(lane).filter((n) => n.id !== id));
  if (activeExitId(lane) === id) setActiveExit(exitNodes(lane)[0]?.id ?? null, lane);
}

export function markExitOk(id: string, lane: Lane = "adult"): void {
  write(KEYS[lane].nodes, exitNodes(lane).map((n) => (n.id === id ? { ...n, lastOk: new Date().toISOString() } : n)));
}

/** The exit point in use right now, if any — from whichever lane is in force. */
export function activeExit(lane: Lane = currentLane()): ExitNode | null {
  if (!geoRelayOn(lane)) return null;
  const id = activeExitId(lane);
  const list = exitNodes(lane);
  return (list.find((n) => n.id === id) ?? list[0] ?? null);
}

/** Appended to every relay call so the server knows our way out. */
export function exitQuery(): string {
  const node = activeExit();
  return node ? `&exit=${encodeURIComponent(node.template)}` : "";
}

/** Build a relay address for one of the relay's modes, with the exit point attached. */
export function relayPath(mode: "list" | "hls" | "seg" | "probe", target: string): string {
  return `/api/public/tv?${mode}=${encodeURIComponent(target)}${exitQuery()}`;
}

/** Try an exit point for real: fetch a tiny known-good file through it. */
export async function testExit(node: ExitNode, lane: Lane = "adult"): Promise<{ ok: boolean; detail: string }> {
  const probe = "https://archive.org/metadata/BigBuckBunny_328";
  try {
    const r = await fetch(`/api/public/tv?list=${encodeURIComponent(probe)}&exit=${encodeURIComponent(node.template)}`);
    if (!r.ok) return { ok: false, detail: `exit point answered ${r.status}` };
    const text = await r.text();
    if (!text.includes("BigBuckBunny")) return { ok: false, detail: "exit point returned something unexpected" };
    markExitOk(node.id, lane);
    return { ok: true, detail: "working — blocked channels will use this way out" };
  } catch {
    return { ok: false, detail: "no answer from the exit point" };
  }
}

/**
 * A real end-to-end check of the connection, not just the settings: fetch a
 * live UK channel list and then one channel's own playlist through the lane in
 * force, and report what actually came back.
 */
export async function checkConnection(lane: Lane = currentLane()): Promise<{
  ok: boolean;
  via: string;
  detail: string;
  channels?: number;
}> {
  const node = activeExit(lane);
  const via = node ? `${node.label}${node.country ? ` · ${node.country}` : ""}` : "skye-net.live itself";
  const list = "https://iptv-org.github.io/iptv/countries/uk.m3u";
  const exit = node ? `&exit=${encodeURIComponent(node.template)}` : "";
  try {
    const r = await fetch(`/api/public/tv?list=${encodeURIComponent(list)}${exit}`);
    if (!r.ok) return { ok: false, via, detail: `the channel list answered ${r.status}` };
    const text = await r.text();
    const urls = text.split(/\r?\n/).filter((l) => /^https?:\/\//.test(l.trim()));
    if (!urls.length) return { ok: false, via, detail: "the list came back with no channels in it" };
    const probe = await fetch(`/api/public/tv?hls=${encodeURIComponent(urls[0])}${exit}`);
    if (node && probe.ok) markExitOk(node.id, lane);
    return {
      ok: probe.ok,
      via,
      channels: urls.length,
      detail: probe.ok
        ? `connected — ${urls.length} channels reachable and the first one is streaming`
        : `the list came through (${urls.length} channels) but the first channel answered ${probe.status}`,
    };
  } catch {
    return { ok: false, via, detail: "no answer at all — check the address and try again" };
  }
}
